import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { DayPicker } from 'react-day-picker';
import { format, parse, isValid } from 'date-fns';
import { he } from 'react-day-picker/locale';
import { Calendar, X, ChevronRight, ChevronLeft } from 'lucide-react';
import clsx from 'clsx';
import {
  formatForInput as formatForInputUtil,
  autoFormatDateInput,
  parseInputDate,
} from './DatePicker.utils';

export interface DatePickerProps {
  /** Date value in YYYY-MM-DD format */
  value?: string;
  /** Called with date string in YYYY-MM-DD format */
  onChange: (date: string) => void;
  label?: string;
  error?: string;
  placeholder?: string;
  /** Min date in YYYY-MM-DD format */
  minDate?: string;
  /** Max date in YYYY-MM-DD format */
  maxDate?: string;
  disabled?: boolean;
  required?: boolean;
  /** Show clear button when value is set */
  clearable?: boolean;
  /** Open the calendar on mount, e.g. in a popover that exists only to pick a date */
  defaultOpen?: boolean;
  className?: string;
}

// Brand colors
const BRAND_BLUE = '#3C5FA8';

function parseISODate(dateStr: string | undefined): Date | undefined {
  if (!dateStr) return undefined;
  const date = parse(dateStr, 'yyyy-MM-dd', new Date());
  return isValid(date) ? date : undefined;
}

function formatToISO(date: Date | undefined): string {
  if (!date || !isValid(date)) return '';
  return format(date, 'yyyy-MM-dd');
}

// Hebrew weekday names (short)
const HEBREW_WEEKDAYS = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];

function formatForInput(date: Date | undefined): string {
  return formatForInputUtil(date);
}

export function DatePicker({
  value,
  onChange,
  label,
  error,
  placeholder = 'DD/MM/YYYY',
  minDate,
  maxDate,
  disabled = false,
  required = false,
  clearable = true,
  defaultOpen = false,
  className,
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [displayMonth, setDisplayMonth] = useState<Date>(new Date());
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});

  const selectedDate = parseISODate(value);
  const minDateParsed = parseISODate(minDate);
  const maxDateParsed = parseISODate(maxDate);

  // Validate date against constraints
  const validateDate = (date: Date | undefined): string | null => {
    if (!date) return null;
    if (minDateParsed && date < minDateParsed) {
      return `התאריך חייב להיות אחרי ${formatForInput(minDateParsed)}`;
    }
    if (maxDateParsed && date > maxDateParsed) {
      return `התאריך חייב להיות לפני ${formatForInput(maxDateParsed)}`;
    }
    return null;
  };

  // Sync input value with prop value
  useEffect(() => {
    if (!isFocused) {
      setInputValue(formatForInput(selectedDate));
      setInputError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isFocused]);

  // Sync display month with selected date
  useEffect(() => {
    if (selectedDate) {
      setDisplayMonth(selectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Position popover
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const popoverHeight = 360;

      const showAbove = spaceBelow < popoverHeight && spaceAbove > spaceBelow;

      setPopoverStyle({
        position: 'fixed',
        ...(showAbove
          ? { bottom: `${viewportHeight - rect.top + 4}px` }
          : { top: `${rect.bottom + 4}px` }),
        right: `${window.innerWidth - rect.right}px`,
        zIndex: 1000,
      });
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleSelect = (date: Date | undefined) => {
    onChange(formatToISO(date));
    setInputValue(formatForInput(date));
    setInputError(null);
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setInputValue('');
    setInputError(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawVal = e.target.value;

    // Auto-format as user types (only when adding at end)
    const formatted = autoFormatDateInput(rawVal, inputValue);
    setInputValue(formatted);
    setInputError(null);

    // Try to parse when we have enough digits (8 digits = complete date)
    const digits = formatted.replace(/\D/g, '');
    if (digits.length === 8) {
      const parsed = parseInputDate(formatted);
      if (parsed) {
        const validationError = validateDate(parsed);
        if (validationError) {
          setInputError(validationError);
        } else {
          onChange(formatToISO(parsed));
          // Format nicely after successful parse
          setInputValue(formatForInput(parsed));
        }
      } else {
        setInputError('תאריך לא תקין (DD/MM/YYYY)');
      }
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);

    if (!inputValue) {
      setInputError(null);
      return;
    }

    const parsed = parseInputDate(inputValue);
    if (!parsed) {
      setInputError('תאריך לא תקין (DD/MM/YYYY)');
      return;
    }

    const validationError = validateDate(parsed);
    if (validationError) {
      setInputError(validationError);
      return;
    }

    // Valid date - format nicely and update
    setInputError(null);
    setInputValue(formatForInput(parsed));
    onChange(formatToISO(parsed));
  };

  const hasError = error || inputError;

  // Focus ring style
  const focusRingStyle: React.CSSProperties =
    isFocused || isOpen
      ? {
          boxShadow: `0 0 0 2px ${hasError ? '#f43f5e' : BRAND_BLUE}`,
          borderColor: 'transparent',
        }
      : {};

  return (
    <div className={clsx('w-full', className)}>
      {label && (
        <label className="mb-1 block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="mr-1 text-rose-500">*</span>}
        </label>
      )}

      <div ref={containerRef} className="relative">
        <div
          style={focusRingStyle}
          className={clsx(
            'min-h-[44px] w-full rounded-lg border px-3 py-2.5 shadow-sm md:min-h-0 md:py-2',
            'flex items-center gap-2 text-base',
            'transition-colors',
            disabled && 'cursor-not-allowed bg-gray-100 opacity-60',
            hasError ? 'border-rose-300' : 'border-gray-300',
            !disabled && !hasError && 'hover:border-gray-400'
          )}
        >
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            className="relative flex-shrink-0 text-gray-400 transition-colors before:absolute before:top-1/2 before:left-1/2 before:size-10 before:-translate-x-1/2 before:-translate-y-1/2 before:content-[''] hover:text-gray-600"
            aria-label="פתח לוח שנה"
          >
            <Calendar size={18} />
          </button>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => {
              setIsFocused(true);
              if (!disabled) setIsOpen(true);
            }}
            onBlur={handleInputBlur}
            placeholder={placeholder}
            disabled={disabled}
            className={clsx(
              'min-w-0 flex-1 border-none bg-transparent text-right outline-none',
              'text-gray-900 placeholder:text-gray-400',
              disabled && 'cursor-not-allowed'
            )}
            dir="ltr"
          />
          {clearable && value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="relative flex-shrink-0 rounded-full p-1 transition-colors before:absolute before:top-1/2 before:left-1/2 before:size-10 before:-translate-x-1/2 before:-translate-y-1/2 before:content-[''] hover:bg-gray-100"
              aria-label="נקה תאריך"
            >
              <X size={14} className="text-gray-400" />
            </button>
          )}
        </div>

        {isOpen &&
          createPortal(
            <div
              ref={popoverRef}
              style={popoverStyle}
              className="rounded-xl bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.07),0_12px_32px_rgba(0,0,0,0.12)]"
              dir="rtl"
            >
              <style>{`
                .rdp-root {
                  --rdp-accent-color: ${BRAND_BLUE};
                  --rdp-accent-background-color: ${BRAND_BLUE}1a;
                  --rdp-day-width: 36px;
                  --rdp-day-height: 36px;
                  font-size: 14px;
                }
                .rdp-root {
                  position: relative;
                }
                .rdp-nav {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 12px;
                }
                .rdp-month_caption {
                  position: absolute;
                  top: 0;
                  left: 40px;
                  right: 40px;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  height: 32px;
                  font-weight: 600;
                  font-size: 16px;
                  color: #111827;
                  pointer-events: none;
                }
                .rdp-button_previous,
                .rdp-button_next {
                  width: 32px;
                  height: 32px;
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  border-radius: 8px;
                  border: none;
                  background: transparent;
                  cursor: pointer;
                  color: #6b7280;
                  transition:
                    background-color 0.15s,
                    color 0.15s;
                }
                .rdp-button_previous:hover,
                .rdp-button_next:hover {
                  background: #f3f4f6;
                  color: #111827;
                }
                .rdp-weekdays {
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 8px;
                }
                .rdp-weekday {
                  width: var(--rdp-day-width);
                  text-align: center;
                  font-size: 12px;
                  font-weight: 500;
                  color: #9ca3af;
                }
                .rdp-weeks {
                  display: flex;
                  flex-direction: column;
                  gap: 4px;
                }
                .rdp-week:nth-child(6) {
                  display: none;
                }
                .rdp-week {
                  display: flex;
                  justify-content: space-between;
                }
                .rdp-day {
                  width: var(--rdp-day-width);
                  height: var(--rdp-day-height);
                }
                .rdp-day_button {
                  width: 100%;
                  height: 100%;
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  border-radius: 8px;
                  border: none;
                  background: transparent;
                  cursor: pointer;
                  font-size: 14px;
                  font-variant-numeric: tabular-nums;
                  color: #374151;
                  transition:
                    background-color 0.15s,
                    border-color 0.15s,
                    color 0.15s,
                    box-shadow 0.15s;
                }
                .rdp-day_button:hover {
                  background: #f3f4f6;
                }
                .rdp-day_button:focus {
                  outline: none;
                  box-shadow: 0 0 0 2px ${BRAND_BLUE}40;
                }
                .rdp-selected .rdp-day_button {
                  background: ${BRAND_BLUE} !important;
                  color: white !important;
                  font-weight: 500;
                }
                .rdp-today .rdp-day_button {
                  color: ${BRAND_BLUE};
                  font-weight: 700;
                  background: ${BRAND_BLUE}15;
                  border: 2px solid ${BRAND_BLUE};
                }
                .rdp-today .rdp-day_button:hover {
                  background: ${BRAND_BLUE}25;
                }
                .rdp-today.rdp-selected .rdp-day_button {
                  color: white;
                  background: ${BRAND_BLUE};
                  border-color: ${BRAND_BLUE};
                }
                .rdp-outside .rdp-day_button {
                  color: #d1d5db;
                }
                .rdp-disabled .rdp-day_button {
                  color: #e5e7eb;
                  cursor: not-allowed;
                }
                .rdp-disabled .rdp-day_button:hover {
                  background: transparent;
                }
                .rdp-hidden {
                  visibility: hidden;
                }
              `}</style>
              <DayPicker
                mode="single"
                selected={selectedDate}
                onSelect={handleSelect}
                month={displayMonth}
                onMonthChange={setDisplayMonth}
                locale={he}
                weekStartsOn={0}
                disabled={[
                  ...(minDateParsed ? [{ before: minDateParsed }] : []),
                  ...(maxDateParsed ? [{ after: maxDateParsed }] : []),
                ]}
                showOutsideDays
                fixedWeeks
                formatters={{
                  formatWeekdayName: (date) => HEBREW_WEEKDAYS[date.getDay()],
                }}
                components={{
                  Chevron: ({ orientation }) =>
                    // Flipped for RTL: left arrow goes forward, right arrow goes back
                    orientation === 'left' ? <ChevronRight size={18} /> : <ChevronLeft size={18} />,
                }}
              />
            </div>,
            document.body
          )}
      </div>

      {(error || inputError) && <p className="mt-1 text-sm text-rose-600">{error || inputError}</p>}
    </div>
  );
}

DatePicker.displayName = 'DatePicker';
